import React, { useContext, useState, useEffect } from 'react'
import { useHistory } from "react-router-dom";
import axios from 'axios'

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';

import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Alert } from '@material-ui/lab';


import { CSVLink } from "react-csv";
import { GrDocumentCsv } from 'react-icons/gr'

import { generateReportData } from './generateReportData';
import { AppContext } from '../components/AppContext'

// Server may send chunked response with keepalive; payload is after this marker
const KEEPALIVE_JSON_MARKER = '__JSON__';

function parseReportResponse(data) {
  if (typeof data === 'string') {
    if (data.includes(KEEPALIVE_JSON_MARKER)) {
      const jsonStr = data.split(KEEPALIVE_JSON_MARKER)[1];
      if (jsonStr) return JSON.parse(jsonStr.trim());
    }
    return JSON.parse(data);
  }
  return data;
}

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  formGrid: {
    flexGrow: 1,
    justifyContent: 'space-around',
    padding: 12,
    marginBottom: 10
  }
});

const Dashboard = () => {
  const [formState, setFormState] = useState({ companyIdValid: false, dateValid: true })
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(
    {
      inventoryValidationReport: [],
      estimateErrs: {
        noShipDate: [],
        acceptRejectStatus: []
      }
    })

    const [isOpen, setOpen] = useState(false)
    const [isLoading, setLoading] = useState(false)

  const context = useContext(AppContext)
  const history = useHistory();

  const headers = [
    { label: "ID Name", key: "name" },
    { label: "SKU", key: "sku" },
    { label: "QTY", key: "qty" },
    { label: "Pending QTY", key: "pendingQty" },
    { label: "QTY - Pending", key: "col5" },
  ]

  const classes = useStyles()

  useEffect(() => {
    context.setAuthenticated(true)
  }, [])

  const handleDateChange = e => {
    const d = e.target.value
    const dateValid = !isNaN(Date.parse(d))
    setFormState(prev => ({ ...prev, dateValid }))
    setSelectedDate(d);
  };

  const handlePullReportsData = async () => {
    try {
      if (selectedDate) {
        setLoading(true)
        console.log('🚀 [FRONTEND] Starting report fetch for date:', selectedDate)
        
        // Must match nginx proxy_read_timeout and server (long QB pagination)
        const result = await axios.get(`/customReportData?date=${selectedDate}`, {
          timeout: 600000, // 10 minutes
          responseType: 'text' // chunked keepalive response is not valid JSON until we extract payload
        })
        const data = parseReportResponse(result.data)
        if (data.error) {
          setLoading(false)
          alert(`Error loading report: ${data.error}${data.details ? '. ' + data.details : ''}. Please try again.`)
          return
        }
        console.log('🚀 [FRONTEND] Response received:', {
          hasEstimates: !!data.estimates,
          estimatesCount: data.estimates?.length,
          hasInvoices: !!data.invoices,
          invoicesCount: data.invoices?.length,
          hasInventory: !!data.inventoryValuations,
          inventoryCount: data.inventoryValuations?.length
        })
        if (!data.estimates || !data.inventoryValuations || !data.invoices) {
          throw new Error('Invalid response structure: missing required data')
        }
        const rd = generateReportData(data.estimates, data.inventoryValuations, data.invoices)
        setReport(rd)
        // Report loaded successfully
        setOpen(true)
        setLoading(false)
        console.log('🚀 [FRONTEND] Report generated successfully')
      }
    }
    catch (err) {
      console.error('🚀 [FRONTEND] Error fetching report:', err)
      console.error('🚀 [FRONTEND] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        code: err.code
      })
      
      // Only logout on authentication errors (401), not on other errors
      if (err.response?.status === 401) {
        console.error('🚀 [FRONTEND] Authentication error - logging out')
        context.setAuthenticated(false)
        history.push('/')
      } else {
        // For other errors, show error message but don't logout
        console.error('🚀 [FRONTEND] Non-auth error - keeping user logged in')
        setLoading(false)
        alert(`Error loading report: ${err.message || 'Unknown error'}. Please try again.`)
      }
    }
  }

  const handlePullReportsDataCached = async () => {
    try {
      if (selectedDate) {
        setLoading(true)
        console.log('🚀 [FRONTEND] Starting cached report fetch for date:', selectedDate)
        
        const result = await axios.get(`/customReportDataCached?date=${selectedDate}`, {
          timeout: 600000, // 10 minutes
          responseType: 'text' // chunked keepalive response is not valid JSON until we extract payload
        })
        const data = parseReportResponse(result.data)
        if (data.error) {
          setLoading(false)
          alert(`Error loading cached report: ${data.error}${data.details ? '. ' + data.details : ''}. Please try again.`)
          return
        }
        console.log('🚀 [FRONTEND] Cached response received:', {
          hasEstimates: !!data.estimates,
          estimatesCount: data.estimates?.length,
          hasInvoices: !!data.invoices,
          invoicesCount: data.invoices?.length,
          hasInventory: !!data.inventoryValuations,
          inventoryCount: data.inventoryValuations?.length,
          cacheInfo: data.cacheInfo
        })
        if (!data.estimates || !data.inventoryValuations || !data.invoices) {
          throw new Error('Invalid response structure: missing required data')
        }
        const rd = generateReportData(data.estimates, data.inventoryValuations, data.invoices)
        setReport(rd)
        // Report loaded successfully
        setOpen(true)
        setLoading(false)
        console.log('🚀 [FRONTEND] Cached report generated successfully')
      }
    }
    catch (err) {
      console.error('🚀 [FRONTEND] Error fetching cached report:', err)
      console.error('🚀 [FRONTEND] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        code: err.code
      })
      
      // Only logout on authentication errors (401), not on other errors
      if (err.response?.status === 401) {
        console.error('🚀 [FRONTEND] Authentication error - logging out')
        context.setAuthenticated(false)
        history.push('/')
      } else {
        // For other errors, show error message but don't logout
        console.error('🚀 [FRONTEND] Non-auth error - keeping user logged in')
        setLoading(false)
        alert(`Error loading cached report: ${err.message || 'Unknown error'}. Please try again.`)
      }
    }
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h3" component="h3" gutterBottom>
        Quickbooks Custom Report
          </Typography>


      <Grid container >
        <Grid xs={12} className={classes.formGrid} container spacing={3}>
          <TextField
            required
            id="date"
            label="Date"
            type="date"
            value={selectedDate}
            onChange={(e) => handleDateChange(e)}
            error={!formState.dateValid}
            helperText={!formState.dateValid && "Enter a valid date mm/dd/yyyy"}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={e => handlePullReportsData(e)}
            disabled={!formState.dateValid || isLoading}
            style={{ marginRight: 10 }}
          >
            Pull Report
            {
              isLoading &&
              <CircularProgress color="primary" size='1rem' />
            }
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={e => handlePullReportsDataCached(e)}
            disabled={!formState.dateValid || isLoading}
          >
            Pull Report (Cached)
            {
              isLoading &&
              <CircularProgress color="primary" size='1rem' />
            }
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table className={classes.table} size="small" aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell>ID Name</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">QTY</TableCell>
                  <TableCell align="right">Pending QTY</TableCell>
                  <TableCell align="right">QTY - Pending</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.inventoryValidationReport.map(row => (
                  <TableRow key={row.id}>
                    <TableCell scope="row">{row.name}</TableCell>
                    <TableCell scope="row">{row.sku}</TableCell>
                    <TableCell align="right">{row.qty.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.pendingQty.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.col5.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} style={{ margin: 10 }}>
          <Typography component="h4">
            Estimates with no ship Date
            <br />
            {
              //report.estimateErrs.noShipDate.join(", ")
              report.estimateErrs.noShipDate.map(nsd => <Chip label={nsd} color='primary' variant="outlined" />)
            }
          </Typography>
        </Grid>
        <Grid item xs={12} style={{ margin: 10 }}>
          <Typography component="h4">
            Estimates that have Accepted/Rejected status
            <br />
            {
              //report.estimateErrs.acceptRejectStatus.join(", ")
              report.estimateErrs.acceptRejectStatus.map(ars => <Chip label={ars} color='primary' variant="outlined" />)
            }
          </Typography>
        </Grid>
        <Grid item xs={12} style={{ margin: 10 }}>
          <CSVLink data={report.inventoryValidationReport} headers={headers} filename={`Report ${selectedDate}.csv`}>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              endIcon={<GrDocumentCsv />}
            >
              Download CSV
              </Button>
          </CSVLink>
        </Grid>
      </ Grid>
      <Snackbar open={isOpen} autoHideDuration={6000} onClose={() => setOpen(false)}>
        <Alert onClose={() => setOpen(false)} severity="success">
          Report generated successfully
         </Alert>
      </Snackbar>
    </Container>
  )
}

export default Dashboard
